import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import apiService from '../services/apiService';
import { API_BASE_URL } from '../config/supabase';

interface PaymentSuccessPageProps {
  paymentData?: any;
}

const PaymentSuccessPage: React.FC<PaymentSuccessPageProps> = ({ 
  paymentData: propPaymentData 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Get payment data from navigation state or props
  const paymentData = location.state?.paymentData || propPaymentData;

  const handleNewDonation = () => {
    navigate('/verification');
  };

  const handleDownloadReceipt = async () => {
    try {
      setIsGeneratingPDF(true);
      
      // Get transaction ID
      const transactionId = paymentData.transactionId || paymentData.paymentResponse?.transaction?.id;
      
      if (!transactionId) {
        alert('Transaction ID not found. Cannot download receipt.');
        setIsGeneratingPDF(false);
        return;
      }

      // Check if receipt PDF exists by fetching transaction status
      try {
        const response = await apiService.get(`/transactions/status/${transactionId}`);
        if (!response.success || !response.data) {
          alert('Transaction not found. Cannot download receipt.');
          setIsGeneratingPDF(false);
          return;
        }
        
        if (!response.data.receipt_pdf_url) {
          alert('Receipt PDF is not available yet. Please try again later.');
          setIsGeneratingPDF(false);
          return;
        }
      } catch (error) {
        console.error('Error fetching transaction data:', error);
        alert('Failed to fetch receipt. Please try again.');
        setIsGeneratingPDF(false);
        return;
      }

      // Download PDF using backend API endpoint (bypasses ngrok HTML warning)
      try {
        console.log('📥 Downloading PDF for transaction:', transactionId);
        
        // Use backend API endpoint to serve PDF directly (bypasses ngrok HTML warning)
        const receiptUrl = `${API_BASE_URL}/transactions/receipt/${transactionId}`;
        
        // Fetch PDF from backend API
        const response = await fetch(receiptUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/pdf',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: response.statusText }));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        // Verify it's actually a PDF
        const contentType = response.headers.get('content-type');
        if (contentType && !contentType.includes('application/pdf')) {
          throw new Error(`Expected PDF but received ${contentType}. The file may not be available.`);
        }

        // Get PDF blob
        const blob = await response.blob();
        
        // If blob is empty or too small, might be an error
        if (blob.size < 100) {
          throw new Error('PDF file appears to be empty or corrupted');
        }
        
        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('content-disposition');
        let fileName = `Receipt_${transactionId}.pdf`;
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (fileNameMatch) {
            fileName = fileNameMatch[1];
          }
        }
        
        // Create download link from blob
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup blob URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
        
        console.log('✅ PDF downloaded successfully');
      } catch (downloadError) {
        console.error('❌ Error downloading PDF:', downloadError);
        const errorMessage = downloadError instanceof Error ? downloadError.message : 'Unknown error occurred';
        alert(`Failed to download receipt: ${errorMessage}. Please try again or contact support.`);
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };


  if (!paymentData) {
    return (
      <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
        <div className="text-center px-4">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3 sm:mb-4">{t('pages.paymentSuccess.noPaymentDataFound')}</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base"
          >
            {t('pages.paymentSuccess.goToMain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full border-2 border-white flex items-center justify-center">
                <img 
                  src="/images/Asset2.png" 
                  alt="Temple Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{t('pages.paymentSuccess.title')}</h1>
            <p className="text-white/90 text-xs sm:text-sm mt-1">{t('pages.paymentSuccess.subtitle')}</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Success Message */}
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">{t('pages.paymentSuccess.donationSuccessful')}</h2>
              <p className="text-gray-600">{t('pages.paymentSuccess.thankYouMessage')}</p>
            </div>

            {/* Transaction Summary */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-800 mb-3">{t('pages.paymentSuccess.transactionSummary')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.transactionId')}:</span>
                  <span className="font-semibold text-gray-800">{paymentData.transactionId || paymentData.paymentResponse?.transaction?.id || `TXN${Date.now().toString().slice(-8)}`}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.orderId')}:</span>
                  <span className="font-semibold text-gray-800">{paymentData.orderId || paymentData.paymentResponse?.order?.id || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.amount')}:</span>
                  <span className="font-semibold text-gray-800">₹{paymentData.amountData?.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.quantity')}:</span>
                  <span className="font-semibold text-gray-800">{paymentData.amountData?.quantity} g</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.ratePerGram')}:</span>
                  <span className="font-semibold text-gray-800">₹{paymentData.amountData?.ratePerGram?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.donationType')}:</span>
                  <span className="font-semibold text-gray-800">{paymentData.amountData?.donationType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.paymentMethod')}:</span>
                  <span className="font-semibold text-gray-800">{paymentData.gateway}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.status')}:</span>
                  <span className="font-semibold text-green-600">{t('pages.paymentSuccess.completed')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.date')}:</span>
                  <span className="font-semibold text-gray-800">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">{t('pages.paymentSuccess.devoteeDetails')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.name')}:</span>
                  <span className="font-semibold text-gray-800">{paymentData.customerData?.fullName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('pages.paymentSuccess.mobile')}:</span>
                  <span className="font-semibold text-gray-800">+91 {paymentData.customerData?.mobileNumber}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleDownloadReceipt}
                disabled={isGeneratingPDF}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isGeneratingPDF ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download Receipt
                  </>
                )}
              </button>
              <button
                onClick={handleNewDonation}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                {t('pages.paymentSuccess.makeAnotherDonation')}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
